import React, { useEffect, useRef, useState } from 'react';
import { BellAlertIcon } from '@heroicons/react/24/outline';
import { useAlertSystemOptional } from '../context/AlertSystemContext';
import { Alert, AlertButton, AlertsCenterButtonProps } from '../types';
import './alerts-center.css';

const TYPE_COLORS: Record<Alert['type'], { border: string; bg: string; text: string }> = {
  info: { border: '#2196f3', bg: '#e3f2fd', text: '#0d47a1' },
  warning: { border: '#ff9800', bg: '#fff3e0', text: '#e65100' },
  error: { border: '#f44336', bg: '#ffebee', text: '#b71c1c' },
  success: { border: '#4caf50', bg: '#e8f5e9', text: '#1b5e20' },
  aida: { border: '#d02591', bg: '#fdf0f8', text: '#7a1456' },
};

const handleAlertButtonClick = (button: AlertButton, alertId: string, dismissAlert: (id: string) => void) => {
  switch (button.action) {
    case 'link':
      if (button.url) {
        if (button.dismissOnClick) {
          dismissAlert(alertId);
          setTimeout(() => {
            window.location.href = button.url as string;
          }, 150);
        } else {
          window.location.href = button.url;
        }
      }
      break;
    case 'callback':
      button.callback?.();
      if (button.dismissOnClick) {
        dismissAlert(alertId);
      }
      break;
    case 'modal':
      if (button.modalContent) {
        window.dispatchEvent(
          new CustomEvent('openAlertModal', {
            detail: button.modalContent,
          }),
        );
      }
      if (button.dismissOnClick) {
        dismissAlert(alertId);
      }
      break;
  }
};

export const AlertsCenterButton: React.FC<AlertsCenterButtonProps> = ({ navIconClass = '' }) => {
  const alertSystem = useAlertSystemOptional();
  const [isOpen, setIsOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  if (!alertSystem || alertSystem.isLoading) return null;

  const { allAlerts, alertCount, isAlertDismissed, dismissAlert } = alertSystem;

  if (allAlerts.length === 0) return null;

  return (
    <div className="nav-alerts-center-wrap" ref={wrapRef}>
      <button
        type="button"
        className="nav-alerts-center-trigger"
        onClick={() => setIsOpen(prev => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={alertCount > 0 ? `Alerts (${alertCount} unread)` : 'Alerts'}
      >
        <span className="nav-alerts-center-icon-wrap">
          <BellAlertIcon className={`nav-icon h-6 w-6 ${navIconClass}`} />
          {alertCount > 0 && (
            <span className="nav-alerts-center-badge">{alertCount > 9 ? '9+' : alertCount}</span>
          )}
        </span>
      </button>

      {isOpen && (
        <div className="nav-alerts-center-panel" role="dialog" aria-label="Alerts">
          <div className="nav-alerts-center-header">
            <h3>Alerts</h3>
            {alertCount > 0 && <span className="nav-alerts-center-unread">{alertCount} unread</span>}
          </div>

          <div className="nav-alerts-center-list">
            {allAlerts.map(alert => {
              const dismissed = isAlertDismissed(alert.id);
              const colors = TYPE_COLORS[alert.type];

              return (
                <article
                  key={alert.id}
                  className={`nav-alerts-center-item${dismissed ? ' is-dismissed' : ''}`}
                  style={{ borderLeftColor: colors.border }}
                >
                  <div className="nav-alerts-center-item-top">
                    <div className="nav-alerts-center-item-body">
                      {alert.type === 'aida' && (
                        <img src="/static/images/eyesAida.svg" alt="" className="nav-alerts-center-aida" />
                      )}
                      <div>
                        <div className="nav-alerts-center-item-title-row">
                          <h4 style={{ color: colors.text }}>{alert.title}</h4>
                          <span className={`nav-alerts-center-tag ${dismissed ? 'is-read' : 'is-unread'}`}>
                            {dismissed ? 'Read' : 'Unread'}
                          </span>
                        </div>
                        <p style={{ color: colors.text }}>{alert.message}</p>
                      </div>
                    </div>
                    {!dismissed && (
                      <button
                        type="button"
                        className="nav-alerts-center-dismiss"
                        aria-label="Dismiss alert"
                        onClick={() => dismissAlert(alert.id)}
                      >
                        ×
                      </button>
                    )}
                  </div>

                  {alert.buttons && alert.buttons.length > 0 && (
                    <div className="nav-alerts-center-actions">
                      {alert.buttons.map((button, index) => (
                        <button
                          key={`${alert.id}-btn-${index}`}
                          type="button"
                          className={`nav-alerts-center-action nav-alerts-center-action--${button.style || 'primary'}`}
                          style={
                            button.style === 'primary' || !button.style
                              ? { background: colors.border, color: '#fff' }
                              : button.style === 'secondary'
                                ? { color: colors.text, borderColor: colors.border }
                                : { color: colors.border }
                          }
                          onClick={() => {
                            handleAlertButtonClick(button, alert.id, dismissAlert);
                            if (button.action === 'link' || button.action === 'modal') {
                              setIsOpen(false);
                            }
                          }}
                        >
                          {button.text}
                        </button>
                      ))}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
